/**
 * Themes within a single city.
 *
 * The product used to string four cities together by high-speed rail. With
 * three tier-one cities that are an hour apart by plane, that framing stopped
 * making sense — nobody rides a train from Beijing to Shenzhen for the view.
 * A "route" is now a way of spending several days inside one city.
 *
 * The Route shape is unchanged and `cities` holds exactly one entry, so the
 * planner and itinerary screens keep working without a rewrite.
 */
import type { ActiveCity } from "./market.js";
import type { Route } from "./types.js";

export interface CityTheme extends Route {
  key: string;
  city: ActiveCity;
  /** Experiences that define this theme; used to bias the day plan. */
  anchors: string[];
  /** What kind of traveller this is for, shown under the theme name. */
  forEn: string;
  forCn: string;
}

export const THEMES: CityTheme[] = [
  // ------------------------------------------------------------- 上海
  {
    key: "sh-lanes",
    city: "上海",
    cities: ["上海"],
    name: "The Lane and the Bund",
    cn: "弄堂与外滩",
    anchors: ["shanghai-breakfast", "french-concession", "tianzifang", "the-bund", "yu-garden"],
    forEn: "You want the city at street level, then the skyline as a reward.",
    forCn: "想先走进街巷，再把天际线当作奖励。",
  },
  {
    key: "sh-neon",
    city: "上海",
    cities: ["上海"],
    name: "Neon and River",
    cn: "霓虹与江",
    anchors: ["lujiazui-skydeck", "huangpu-cruise", "the-bund", "nanjing-road", "shanghai-bars"],
    forEn: "You are here for the 21st century, ideally after dark.",
    forCn: "为二十一世纪而来，最好是天黑以后。",
  },
  {
    key: "sh-art",
    city: "上海",
    cities: ["上海"],
    name: "The Museum Mile",
    cn: "美术馆一英里",
    anchors: ["west-bund-art", "power-station-art", "m50-art", "shanghai-museum", "zhujiajiao"],
    forEn: "Galleries, quiet lanes, and a water town when the city gets loud.",
    forCn: "美术馆、安静的街区，城市太吵时去一趟水乡。",
  },
  // ------------------------------------------------------------- 北京
  {
    key: "bj-axis",
    city: "北京",
    cities: ["北京"],
    name: "The Imperial Axis",
    cn: "帝国中轴",
    anchors: [
      "forbidden-city",
      "temple-of-heaven",
      "jingshan-sunset",
      "beihai-park",
      "summer-palace",
    ],
    forEn: "Six hundred years of capital, walked end to end.",
    forCn: "六百年的都城，从南到北走一遍。",
  },
  {
    key: "bj-wall",
    city: "北京",
    cities: ["北京"],
    name: "Wall and Hutong",
    cn: "长城与胡同",
    anchors: ["greatwall-mutianyu", "hutong-crawl", "nanluoguxiang", "houhai", "beijing-duck"],
    forEn: "One big day out of the city, and evenings in the alleys.",
    forCn: "用一整天出城看长城，晚上回到胡同里。",
  },
  {
    key: "bj-now",
    city: "北京",
    cities: ["北京"],
    name: "Beijing Now",
    cn: "当代北京",
    anchors: ["798-art", "olympic-park", "capital-museum", "beijing-duck", "national-museum"],
    forEn: "Contemporary art and food, with the imperial city as backdrop.",
    forCn: "当代艺术和吃，把故宫当背景。",
  },
  // ------------------------------------------------------------- 深圳
  {
    key: "sz-coast",
    city: "深圳",
    cities: ["深圳"],
    name: "The Eastern Coast",
    cn: "东部海岸线",
    anchors: ["dameisha", "dapeng-fortress", "shenzhen-bay", "dongxichong"],
    forEn: "Beaches, a Ming garrison town, and a coastline most visitors never see.",
    forCn: "海滩、明代所城，还有多数人不知道的海岸线。",
  },
  {
    key: "sz-made",
    city: "深圳",
    cities: ["深圳"],
    name: "Made Here",
    cn: "制造之城",
    anchors: ["huaqiangbei", "oct-loft", "lianhuashan", "shekou-seaworld"],
    forEn: "How a fishing village became the hardware capital of the world, in 40 years.",
    forCn: "一个渔村用四十年变成世界硬件之都。",
  },
  {
    key: "sz-easy",
    city: "深圳",
    cities: ["深圳"],
    name: "Easy Shenzhen",
    cn: "轻松深圳",
    anchors: ["window-of-world", "shenzhen-bay", "dameisha", "shenzhen-food"],
    forEn: "Flat, green and low-effort — built for travelling with kids.",
    forCn: "平坦、绿、不费力 —— 为带孩子出行准备的。",
  },
];

export const themesFor = (city: ActiveCity): CityTheme[] => THEMES.filter((t) => t.city === city);
export const themeByKey = (key: string): CityTheme | undefined => THEMES.find((t) => t.key === key);
